<script setup lang="ts">
// icons
import { faChevronRight, faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';

// validation
import { defineRule, useForm, useField } from 'vee-validate';
import { email, required } from '@vee-validate/rules';

import links from '@/assets/json/footer.json';

// validation rules
defineRule('email', email);
defineRule('required', required);

definePageMeta({
  layout: 'simple',
});

/********************
 * COMPOSITIONS      *
 ********************/
const loadingStore = useLoadingStore();
const localePath = useLocalePath();
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const { $gettext, $pgettext, interpolate } = useGettext();
const { i18nTermsOfUseLink } = useI18nMeta();
const { locale } = useI18n();

/********************
 * REFS & VARS       *
 ********************/
const externalLinkIcon = ref<HTMLElement | null>(null);
const errors = ref(null);
const socialLoginError = ref<string | null>(null);
const rememberMe = ref(false);

const i18nGetRememberMe = computed(() => {
  return $gettext('Remember me?');
});

const i18nGetPasswordLabel = computed(() => {
  return $gettext('Password');
});

const i18nGetEmailLabel = computed(() => {
  return $gettext('E-Mail');
});

const i18nGetLoginButtonLabel = computed(() => {
  return loadingStore.isLoading('login') ? $gettext('Please wait…') : $pgettext('Account form button', 'Login');
});

const i18nGetRegisterInText = computed(() => {
  return $gettext('Sign up!');
});

const i18nOwnerCopy = computed(() => {
  return $gettext('In your administration area you can manage your details and pictures on camping.info.');
});

function getI18nSocialLoginError(code: string): string {
  if (code === 'email-missing') {
    const errorMessage = $gettext(
      'Your email address has not been provided. Please revoke the camping.info Facebook app as described in the %{helpCenterLink} and try again afterwards.',
    );
    const helpCenterText = $pgettext('Title of the Facebook Help Center as seen here: https://www.facebook.com/help', 'Facebook Help Center');
    let externalLinkIconHTML = '';
    if (externalLinkIcon.value) {
      externalLinkIconHTML = externalLinkIcon.value.children[0]?.innerHTML || '';
      if (externalLinkIconHTML) {
        externalLinkIconHTML = ` <span class="external-link-icon">${externalLinkIconHTML}</span>`;
      }
    }
    const helpCenterLink = `<a href="https://www.facebook.com/help/170585223002660" target="_blank" class="text-nowrap">${helpCenterText}${externalLinkIconHTML}</a>`;
    return interpolate(errorMessage, { helpCenterLink }, true);
  }
  return $gettext('An error occurred. Please try again later.');
}

// form
const { validate } = useForm({
  validationSchema: {
    email: 'required|email',
    password: 'required',
  },
});

const { value: emailValue, errorMessage: emailError } = useField<string>('email');
const { value: passwordValue, errorMessage: passwordError } = useField<string>('password');

/********************
 * FUNCTIONS         *
 ********************/
function check(value: boolean) {
  rememberMe.value = value; // e.target.checked;
}

function getLink(name, fallbackToEnglish = true) {
  const link = links.footer[name];

  if (link && link[locale.value]) {
    return link[locale.value];
  }

  if (fallbackToEnglish && link && link.en) {
    return link.en;
  }

  if (link && link.civ1 && typeof link.civ1 === 'string') {
    return link.civ1.replace('$lang', locale.value);
  }

  return null;
}

async function submit() {
  const form = { email: emailValue.value?.trim(), password: passwordValue.value };
  const { valid: isValid } = await validate();

  if (!isValid) {
    return false;
  }

  userStore
    .login({
      lang: locale.value,
      email: form.email,
      password: form.password,
      remember: rememberMe.value,
    })
    .then((response) => {
      if (response !== null) {
        const query = { ...route.query };

        if (query.redirect) {
          return navigateTo(localePath({ path: query.redirect }));
        } else {
          return navigateTo(localePath({ name: 'index' }));
        }
      }
    })
    .catch((e) => {
      errors.value = e.response?._data?.non_field_errors?.map(e => e.message).join();
    });

  return true;
}

onMounted(() => {
  const error = route.query.error;
  if (error) {
    const query = Object.assign({}, route.query);
    delete query.error;
    router.replace({ query });
    socialLoginError.value = `${error}`;
  }
});
</script>

<template>
  <client-only>
    <section class="mx-auto w-full max-w-[480px] px-4 py-0">
      <transition
        name="slide-fade"
        appear
      >
        <div class="login-form">
          <h1 class="mb-4 text-xl">
            {{ $pgettext('Account form title', 'Login') }}
          </h1>
          <div ref="externalLinkIcon" class="hidden">
            <CiAwesomeIcon
              :icon="faArrowUpRightFromSquare"
              ratio="0.6"
            />
          </div>
          <div v-if="socialLoginError" class="mb-6 rounded-md border border-danger bg-[#fef0f0] px-4 py-3 text-[#dc0911]">
            <div v-html="getI18nSocialLoginError(socialLoginError)" />
          </div>

          <CiSocialLogin />

          <form
            novalidate
            @submit.prevent="submit"
          >
            <InputTextfield
              id="email"
              v-model="emailValue"
              :error="!!emailError"
              :label="i18nGetEmailLabel"
              inverted
              name="email"
              type="email"
            />
            <InputTextfield
              id="password"
              v-model="passwordValue"
              :error="!!passwordError"
              :label="i18nGetPasswordLabel"
              inverted
              name="password"
              autocomplete="on"
              type="password"
            >
              <nuxt-link
                class="absolute right-0 top-[0.15rem] block text-sm"
                :to="localePath({ name: 'account-reset-password' })"
              >
                {{ $gettext('Forgot password?') }}
              </nuxt-link>
            </InputTextfield>

            <InputCheckbox
              id="remember-me"
              :value="rememberMe"
              :label="i18nGetRememberMe"
              @input="check"
            />

            <CiInfoPanel
              v-if="errors"
              :condition="true"
              type="danger"
            >
              <CiDjangoErrorMessages :errors="errors" />
            </CiInfoPanel>

            <CiButton
              :disabled="loadingStore.isLoading('login')"
              class="button icon-absolute w-full"
              :class="[loadingStore.isLoading('login') ? 'button--disabled' : 'button--primary']"
              type="submit"
            >
              <span>{{ i18nGetLoginButtonLabel }}</span>
            </CiButton>

            <div class="login-form__to-registration mt-4 text-center">
              <p
                v-html="$gettext('With the login I accept the current %{link}.', { link: i18nTermsOfUseLink }, true)"
              />
              <p>
                <span>{{ $gettext('No account?') }}</span>&nbsp;
                <nuxt-link
                  :to="localePath({ name: 'account-register', query: route.query })"
                  class="whitespace-nowrap"
                >
                  {{ i18nGetRegisterInText }}
                </nuxt-link>
              </p>
            </div>
          </form>
        </div>
      </transition>
    </section>

    <hr class="mb-6 mt-12 border border-gray-20" />

    <section class="mx-auto w-full max-w-[480px] px-4 py-0">
      <transition
        name="slide-fade"
        appear
      >
        <div class="login-form">
          <h2 class="mb-6 text-xl">
            {{ $gettext('You are campsite owner?') }}
          </h2>
          <p>{{ i18nOwnerCopy }}</p>
          <a
            :href="getLink('ownerLogIn')"
            class="button button--primary-outline icon-absolute w-full"
            target="_blank"
            rel="noopener"
          >
            <span>{{ $gettext('Owner Login') }}</span>
            <CiAwesomeIcon
              :icon="faChevronRight"
              ratio="0.6"
            />
          </a>
        </div>
      </transition>
    </section>
  </client-only>
</template>

<style lang="css">
.external-link-icon {
  svg {
    fill: currentColor;
    @apply inline;
  }
}
</style>
